import {
    createRouter,
    createWebHistory,
    type RouteLocationNormalized,
    type RouteLocationRaw,
    type RouteRecordRaw,
} from 'vue-router';
import { useAuthStore } from '../stores/authStore';
import { useMatterStore } from '../stores/matterStore';
import { useSessionStore } from '../stores/sessionStore';
import { trackPage } from '../analytics';
import AuthCallback from '../components/auth/AuthCallback.vue';
import LoginSplash from '../components/auth/LoginSplash.vue';
import MatterSelection from '../components/matter/MatterSelection.vue';
import MatterPage from '../components/matter/MatterPage.vue';
import TypeManagementPage from '../components/type-management/TypeManagementPage.vue';
import AuditExportPage from '../components/audit/AuditExportPage.vue';
import NarrativesPage from '../components/narrative/NarrativesPage.vue';
import NarrativeDetailPage from '../components/narrative/NarrativeDetailPage.vue';
import FindingsPage from '../components/narrative/FindingsPage.vue';
import NarrativeEditorPage from '../components/narrative/NarrativeEditorPage.vue';
import PocRenderPage from '../components/poc/PocRenderPage.vue'; // THROWAWAY: rendering spike

const routes: RouteRecordRaw[] = [
    {
        path: '/login',
        name: 'login',
        component: LoginSplash,
        meta: { skipAuth: true },
    },
    {
        path: '/login-complete',
        name: 'login-complete',
        component: AuthCallback,
        meta: { skipAuth: true },
    },
    {
        path: '/',
        name: 'matter-selection',
        component: MatterSelection,
    },
    {
        path: '/matters/:matterId',
        name: 'matter',
        component: MatterPage,
        meta: { requiresMatter: true },
    },
    {
        path: '/matters/:matterId/sessions/:sessionId',
        name: 'session',
        component: MatterPage,
        meta: { requiresMatter: true, requiresSession: true },
    },
    {
        path: '/matters/:matterId/types',
        name: 'types',
        component: TypeManagementPage,
        meta: { requiresMatter: true },
    },
    // Narratives workspace — mock shell, no backend yet. Matter-gated like the
    // dialogues/types pages so it hydrates the matter store on a cold link.
    {
        path: '/matters/:matterId/narratives',
        name: 'narratives',
        component: NarrativesPage,
        meta: { requiresMatter: true },
    },
    // Narrative detail / editor — the open document with its Pages sidebar.
    // Backed by the stub narratives API (routers/narratives_router.py); the
    // narrative id is in the URL so it deep-links. Matter-gated on cold links.
    {
        path: '/matters/:matterId/narratives/:narrativeId',
        name: 'narrative-detail',
        component: NarrativeDetailPage,
        meta: { requiresMatter: true },
    },
    // Findings — sibling tab of the Narratives workspace (NarrativeTabs).
    // Mock shell, no backend yet; matter-gated like the narratives page.
    {
        path: '/matters/:matterId/findings',
        name: 'findings',
        component: FindingsPage,
        meta: { requiresMatter: true },
    },
    // Hidden, unlinked audit-export tool. No nav points here — reachable
    // only by typing the URL. Auth-gated like every non-skipAuth route.
    {
        path: '/audit/export',
        name: 'audit-export',
        component: AuditExportPage,
    },
    // Hidden, unlinked Editor.js POC. No nav points here — reachable only
    // by typing the URL. Auth-gated like every non-skipAuth route.
    {
        path: '/narratives/demo',
        name: 'narrative-demo',
        component: NarrativeEditorPage,
    },
    // THROWAWAY: GroupDocs rendering spike. Unlinked and auth-gated like
    // every non-skipAuth route (the backend POC endpoints are gated too).
    {
        path: '/poc/render',
        name: 'poc-render',
        component: PocRenderPage,
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

/**
 * Auth guard. Exported so it can be unit-tested without spinning up
 * the real router (which has hard dependencies on window history).
 *
 * - /login and /login-complete are exempt (they're part of the auth
 *   flow itself).
 * - If we have a token in sessionStorage, fetch /auth/me once to load
 *   the user; proceed when that succeeds.
 * - No token, or /auth/me returns 401 → redirect to the /login splash
 *   (which then kicks off /auth/login on click). We send the user to
 *   the splash rather than straight to the IdP so a click is required —
 *   this is also the page they land on after signing out.
 *
 * Returns true to allow the nav, or a route location to redirect to the
 * splash. The originally-requested path rides along as ?returnTo= so the
 * splash can hand it to login() for deep-link restoration after auth.
 */
export async function authGuard(
    to: RouteLocationNormalized,
): Promise<true | RouteLocationRaw> {
    if (to.meta.skipAuth) return true;

    const authStore = useAuthStore();

    if (!authStore.token) {
        return loginRedirect(to);
    }

    if (authStore.user !== null) {
        return true;
    }

    // Have a token, no cached user yet — verify with /auth/me.
    const user = await authStore.loadCurrentUser();
    if (user !== null) {
        return true;
    }

    // Token was rejected; loadCurrentUser already cleared it.
    return loginRedirect(to);
}


// Build the redirect to the /login splash, carrying the originally-requested
// path so the splash can restore it after sign-in.
function loginRedirect(to: RouteLocationNormalized): RouteLocationRaw {
    const returnTo = returnPathFor(to);
    return {
        name: 'login',
        query: returnTo ? { returnTo } : {},
    };
}


// Skip the root path — it's the default landing after sign-in, so
// stashing it just bloats the URL/sessionStorage with a no-op.
function returnPathFor(to: RouteLocationNormalized): string | undefined {
    return to.fullPath === '/' ? undefined : to.fullPath;
}

router.beforeEach(authGuard);

// Hydrate the matter + session stores from URL params on routes that
// need them. Cold deep links (paste-into-new-tab, refresh) arrive with
// empty stores; this guard resolves each id against its list and either
// selects it or bounces one level up.
//
// The guard also enforces the inverse: when a route doesn't include a
// :sessionId, any currently-selected session is cleared so the URL stays
// the source of truth for what's loaded.
router.beforeEach(async (to) => {
    const matterStore = useMatterStore();

    if (!to.meta.requiresMatter) {
        // Same inverse rule as sessions below: a route with no :matterId
        // (matter picker, auth pages) means no matter is selected. Without
        // this, paths that reach the picker other than the header icon —
        // the locked-matter escape hatch, browser back — leave the old
        // matter in the store and the header keeps showing its name.
        if (matterStore.currentMatter !== null) {
            matterStore.clearCurrentMatter();
        }
        return;
    }

    const matterId = to.params.matterId;
    if (typeof matterId !== 'string' || !matterId) {
        return { name: 'matter-selection' };
    }

    try {
        const ok = await matterStore.ensureMatterLoaded(matterId);
        if (!ok) return { name: 'matter-selection' };
    } catch {
        // Matter-list fetch failed — error is already on matterStore.error.
        // Send the user back to the picker so the failure surfaces there.
        return { name: 'matter-selection' };
    }

    const sessionStore = useSessionStore();
    if (to.meta.requiresSession) {
        const sessionId = to.params.sessionId;
        if (typeof sessionId !== 'string' || !sessionId) {
            return { name: 'matter', params: { matterId } };
        }
        try {
            const ok = await sessionStore.ensureSessionLoaded(sessionId);
            if (!ok) return { name: 'matter', params: { matterId } };
        } catch {
            return { name: 'matter', params: { matterId } };
        }
    } else if (sessionStore.currentSession !== null) {
        sessionStore.clearCurrentSession();
    }
});

// Pageview tracking for the SPA. PostHog's built-in pageview capture only
// fires on full page loads, so client-side navigations (router.push) are
// invisible without this. We send the route *name* and *path pattern*
// (e.g. "/matters/:matterId") rather than the resolved fullPath, so matter
// and session ids never leak into the analytics payload — consistent with
// the facade's keep-ids-out-of-properties policy. trackPage no-ops when
// analytics is disabled, so this is safe to register unconditionally.
router.afterEach((to) => {
    trackPage(typeof to.name === 'string' ? to.name : to.path, {
        path_pattern: to.matched[to.matched.length - 1]?.path ?? to.path,
    });
});

export default router;
